import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../db/queryKeys'
import { ServerRoutes } from './serverRoutes'

const postJson = async <T>(path: string, body: object = {}): Promise<T> => {
  const response = await fetch(import.meta.env.VITE_BE_SERVER_URL + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    let message = 'Request failed'
    try {
      const errorData = (await response.json()) as { msg?: string }
      if (errorData?.msg) message = errorData.msg
    } catch {
      // non-JSON
    }
    throw new Error(message)
  }
  return (await response.json()) as T
}

export type InboxInsights = {
  needs_review: number
  waiting: number
  waiting_over_24h: number
  avg_minutes_to_draft: number | null
  avg_minutes_to_send: number | null
  sent_7d: number
}

export type InboxStatusPayload = {
  configured: boolean
  accounts: number
  threads: number
  needs_review: number
}

export const useEmailInboxSync = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (backfill?: boolean) =>
      postJson<{
        accounts: number
        ingested: number
        inbound: number
        pages: number
      }>(ServerRoutes.emailInboxSync, { backfill: Boolean(backfill) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.emailInbox] })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxStatus],
      })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxInsights],
      })
    },
  })
}

export const useEmailInboxSend = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { suggested_reply_id: string; body_text?: string }) =>
      postJson<{ sent: boolean }>(ServerRoutes.emailInboxSend, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.emailInbox] })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxInsights],
      })
    },
  })
}

export const useEmailInboxInsights = (enabled: boolean) =>
  useQuery({
    queryKey: [queryKeys.emailInboxInsights],
    enabled,
    queryFn: () => postJson<InboxInsights>(ServerRoutes.emailInboxInsights),
  })

export const useEmailInboxStatus = () =>
  useQuery({
    queryKey: [queryKeys.emailInboxStatus],
    queryFn: () => postJson<InboxStatusPayload>(ServerRoutes.emailInboxStatus),
  })
